import { useEffect, useState } from "react";
import { loadEntrySnapshot } from "./useReforecastScrub.js";
import { asPreviousForecast, previousVersionEntry } from "../lib/report/previousVersion.js";

// Two forecasts can share a manual version's moment (copies of one panel fire), so a manual
// version is known by its own snapshot object and a scheduled one by its report row.
const snapshotIds = new WeakMap();
let nextSnapshotId = 0;

/** A version entry's identity for the cache below, or null. */
export function previousVersionKey(entry) {
  if (!entry) return null;
  if (entry.rowKey) return `row:${entry.rowKey}`;
  const snap = entry.snapshot;
  if (snap && typeof snap === "object") {
    if (!snapshotIds.has(snap)) snapshotIds.set(snap, ++nextSnapshotId);
    return `snapshot:${snapshotIds.get(snap)}`;
  }
  return `moment:${entry.moment}`;
}

/**
 * The forecast version just before the one a report shows, loaded for comparison, shaped like
 * previousForecast() ({p, at, contract}). Null while loading, when there is no earlier version,
 * or when it can't be read.
 *
 * @param {{ entries: Array<object>|null, momentIso: string|null }} deps the report's version
 *   list (null when the report has none) and its scrubbed moment (null when latest)
 * @returns {{p: number|null, at: string|null, contract: object}|null}
 */
export function usePreviousVersion({ entries, momentIso }) {
  const entry = previousVersionEntry(entries, momentIso);
  const key = previousVersionKey(entry);
  const [state, setState] = useState({ key: null, prev: null });
  useEffect(() => {
    if (!entry || state.key === key) return undefined;
    let cancelled = false;
    loadEntrySnapshot(entry).then((snap) => {
      if (!cancelled) setState({ key, prev: asPreviousForecast(entry, snap) });
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the entry's identity; `entry` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return entry && state.key === key ? state.prev : null;
}
